import React from 'react';
import { Link } from 'react-router-dom';
import { Snowflake, Wind, Wrench, ShoppingBag, SprayCan, ArrowRight, Phone, CheckCircle, Users, Clock, Award, Star, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';

const HERO_IMAGE = "https://images.pexels.com/photos/5463581/pexels-photo-5463581.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const LOGO_URL = "/assets/logo-le-bleu-des-champs.png";

const services = [
  {
    icon: Snowflake,
    title: 'Climatisation & Chauffage',
    description: 'Installation, entretien et réparation de systèmes de climatisation et de chauffage.',
    color: 'from-blue-500 to-orange-500',
    link: '/services#climatisation-chauffage'
  },
  {
    icon: SprayCan,
    title: 'Nettoyage',
    description: 'Nettoyage professionnel d\'échangeurs d\'air et thermopompes murales et centrales.',
    color: 'from-cyan-500 to-blue-500',
    link: '/services#nettoyage'
  },
  {
    icon: Wind,
    title: 'Ventilation',
    description: 'Systèmes de ventilation, humidificateurs et échangeurs d\'air pour un air sain.',
    color: 'from-teal-500 to-green-500',
    link: '/services#ventilation'
  },
  {
    icon: Wrench,
    title: 'Réparation d\'électroménagers',
    description: 'Réparation de laveuses, sécheuses, lave-vaisselle, cuisinières et réfrigérateurs.',
    color: 'from-purple-500 to-indigo-500',
    link: '/services#reparation-electromenagers'
  },
  {
    icon: ShoppingBag,
    title: 'Électroménagers usagés & Pièces',
    description: 'Vente d\'électroménagers usagés et comptoir de pièces en magasin.',
    color: 'from-amber-500 to-yellow-500',
    link: '/services#vente-pieces'
  }
];

const stats = [
  { icon: Users, value: '8', label: 'Employés dévoués' },
  { icon: Clock, value: '47+', label: 'Années d\'expérience' },
  { icon: Award, value: '5', label: 'Camions de service' },
];

export const HomePage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="hero-gradient relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#00b4d8] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#00d4ff] rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <span className="inline-block px-4 py-2 rounded-full bg-[#00b4d8]/20 text-[#00d4ff] text-sm font-medium mb-6">
                Depuis 1977 à Saint-Césaire
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Créateur de
                <span className="text-[#00d4ff]"> Confort</span>
              </h1>
              <p className="text-xl text-slate-300 mb-8 max-w-lg">
                Votre partenaire de confiance pour tous vos besoins en climatisation, 
                chauffage, ventilation et électroménagers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/soumission">
                  <Button 
                    size="lg" 
                    className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8] text-white px-8 py-6 text-lg w-full sm:w-auto"
                    data-testid="hero-quote-btn"
                  >
                    Demander une soumission
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:+14504693483">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="rounded-full border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg w-full sm:w-auto"
                    data-testid="hero-call-btn"
                  >
                    <Phone className="mr-2 w-5 h-5" />
                    Appelez-nous
                  </Button>
                </a>
              </div>
            </div>

            <div className="hidden lg:block relative">
              <div className="relative animate-pulse-glow rounded-3xl overflow-hidden">
                <img 
                  src={HERO_IMAGE}
                  alt="Air Conditioned for Your Comfort"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Wave bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div 
                  key={index} 
                  className="flex items-center gap-4 p-6 rounded-2xl bg-slate-50 card-hover"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-14 h-14 rounded-xl bg-[#00b4d8]/10 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-[#00b4d8]" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-[#1a3a5c]">{stat.value}</p>
                    <p className="text-slate-500">{stat.label}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00b4d8] font-medium">Nos services</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a5c] mt-2">
              Une expertise complète
            </h2>
            <p className="text-slate-500 mt-4 max-w-2xl mx-auto">
              De l'installation à la réparation, nous offrons une gamme complète de services 
              pour assurer votre confort toute l'année.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-2xl bg-white p-8 shadow-sm hover:shadow-xl transition-all duration-300 card-hover border border-slate-100"
                  data-testid={`service-card-${index}`}
                >
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#1a3a5c] mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-500 mb-4">
                    {service.description}
                  </p>
                  <Link 
                    to={service.link} 
                    className="inline-flex items-center text-[#00b4d8] font-medium hover:text-[#1a3a5c] transition-colors"
                  >
                    En savoir plus
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                size="lg" 
                className="rounded-full bg-[#1a3a5c] hover:bg-[#0f2a4a] text-white px-8"
                data-testid="all-services-btn"
              >
                Voir tous nos services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00b4d8] font-medium">Témoignages</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a5c] mt-2">
              Ce que nos clients disent
            </h2>
            <p className="text-slate-500 mt-4 max-w-2xl mx-auto">
              La satisfaction de nos clients est notre priorité depuis plus de 47 ans
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                name: "Erika Blache",
                location: "Local Guide Google",
                text: "Service courtois, rapide, efficace et honnête. Je suis très contente d'avoir fait appel à leur service, je les recommande. Merci!",
                rating: 5
              },
              {
                name: "Jean-Pierre Leblanc",
                location: "Avis Google",
                text: "Service et travail impeccable. Les techniciens sont arrivés à l'heure. Très courtois et aimables. Ils ont pris le temps d'expliquer avec patience le fonctionnement.",
                rating: 5
              },
              {
                name: "Félix Dandurand",
                location: "Avis Google",
                text: "Je recommande cette entreprise à 100%. Personnel très compétent et installation impeccable selon les règles de l'art. Prix très compétitif. Merci!",
                rating: 5
              },
              {
                name: "MJ T",
                location: "Avis Google",
                text: "Très bon service! Nous avons fait nettoyer nos thermopompes. Professionnel, ils connaissent leur affaire.",
                rating: 5
              },
              {
                name: "Louise Robineau",
                location: "Avis Google",
                text: "Anne-Marie Lorion a fait le nettoyage de ma thermopompe murale. Excellent travail fait avec beaucoup de minutie. Je recommande fortement!",
                rating: 5
              }
            ].map((testimonial, index) => (
              <div 
                key={index}
                className="relative bg-slate-50 rounded-2xl p-8 card-hover"
                data-testid={`testimonial-${index}`}
              >
                {/* Quote icon */}
                <div className="absolute top-6 right-6">
                  <Quote className="w-10 h-10 text-[#00b4d8]/20" />
                </div>
                
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>

                {/* Text */}
                <p className="text-slate-600 mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#1a3a5c] flex items-center justify-center text-white font-semibold">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <p className="font-semibold text-[#1a3a5c]">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/20046691/pexels-photo-20046691.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Équipement HVAC"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-8 -right-8 bg-white rounded-2xl p-6 shadow-lg">
                <p className="text-4xl font-bold text-[#00b4d8]">1977</p>
                <p className="text-slate-500">Année de fondation</p>
              </div>
            </div>

            <div>
              <span className="text-[#00b4d8] font-medium">Notre histoire</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a5c] mt-2 mb-6">
                Une entreprise familiale depuis 2 générations
              </h2>
              <p className="text-slate-600 mb-6">
                Fondée en 1977 par Jean Lebleu à Saint-Césaire, Le Bleu des Champs 
                est une entreprise familiale qui a su évoluer au fil des années tout 
                en conservant ses valeurs d'engagement, de service et de qualité.
              </p>
              <p className="text-slate-600 mb-8">
                Aujourd'hui dirigée par Eric Lebleu, l'entreprise compte 8 employés 
                dévoués et 5 camions de service pour répondre à tous vos besoins.
              </p>
              <Link to="/a-propos">
                <Button 
                  variant="outline" 
                  className="rounded-full border-2 border-[#1a3a5c] text-[#1a3a5c] hover:bg-[#1a3a5c] hover:text-white px-8"
                  data-testid="about-btn"
                >
                  En savoir plus sur nous
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
